/**
 * Cost Display tab — controls what cost information this org's users see in
 * the app. Set show_* flags off (and/or meter mode to "allotment") to hide
 * raw dollar figures from customer-facing surfaces — e.g. during a demo.
 */
import { useState, useEffect } from 'react';
import { api } from '../../../api';

const TOGGLES = [
  { key: 'show_costs', label: 'Show costs (master switch)', hint: 'Off hides all cost/dollar figures.' },
  { key: 'show_cost_breakdown', label: 'Show per-service breakdown', hint: 'Itemized cost-by-service lists.' },
  { key: 'show_processing_costs', label: 'Show processing costs', hint: 'Document extraction / analysis costs.' },
  { key: 'show_llm_costs', label: 'Show chat / LLM costs', hint: 'Per-message and conversation costs.' },
];

export default function OrgDisplayTab({ org, opsRole }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const canMutate = ['admin', 'owner'].includes(opsRole);

  useEffect(() => {
    api.get(`/ops_admin/orgs/${org.id}/pricing-display`)
      .then(d => setConfig(d.config))
      .catch(e => setMsg(`Error: ${e.message}`))
      .finally(() => setLoading(false));
  }, [org.id]);

  async function save(patch, successMsg) {
    setSaving(true); setMsg(null);
    try {
      const d = await api.patch(`/ops_admin/orgs/${org.id}/pricing-display`, patch);
      setConfig(d.config);
      setMsg(successMsg || 'Saved');
    } catch (e) { setMsg(`Error: ${e.message}`); } finally { setSaving(false); }
  }

  if (loading) return <p className="loading-text">Loading cost display config...</p>;
  if (!config) return <p className="error-text">{msg || 'No config'}</p>;

  const costsHidden = !config.show_costs;

  return (
    <div className="ops-tab">
      <div className="ops-section">
        <h3>Demo Mode</h3>
        <p className="info-banner">
          {costsHidden
            ? 'Costs are currently HIDDEN from this org’s users.'
            : 'Costs are currently VISIBLE to this org’s users.'}
        </p>
        {canMutate && (
          <div className="button-row">
            <button
              className="btn btn-primary"
              disabled={saving || costsHidden}
              onClick={() => save(
                { show_costs: false, show_cost_breakdown: false, show_processing_costs: false, show_llm_costs: false, meter_display_mode: 'allotment', reason: 'Demo: hide costs' },
                'Costs hidden — demo mode on',
              )}
            >
              Hide all costs (demo mode)
            </button>
            <button
              className="btn btn-secondary"
              disabled={saving || (!costsHidden && config.meter_display_mode === 'cost')}
              onClick={() => save(
                { show_costs: true, show_cost_breakdown: true, show_processing_costs: true, show_llm_costs: true, meter_display_mode: 'cost', reason: 'Reset: show costs' },
                'Costs visible — demo mode off',
              )}
            >
              Show all costs (reset)
            </button>
          </div>
        )}
      </div>

      <div className="ops-section">
        <h3>Individual Toggles</h3>
        <div className="posture-form">
          {TOGGLES.map(({ key, label, hint }) => (
            <div className="form-group" key={key}>
              <label className="form-label">{label}</label>
              <select
                className="form-input"
                value={config[key] ? 'true' : 'false'}
                disabled={!canMutate || saving}
                onChange={e => save({ [key]: e.target.value === 'true' }, 'Saved')}
              >
                <option value="true">Shown</option>
                <option value="false">Hidden</option>
              </select>
              <span className="form-hint">{hint}</span>
            </div>
          ))}

          <div className="form-group">
            <label className="form-label">Meter display mode</label>
            <select
              className="form-input"
              value={config.meter_display_mode || 'cost'}
              disabled={!canMutate || saving}
              onChange={e => save({ meter_display_mode: e.target.value }, 'Saved')}
            >
              <option value="cost">Cost (dollar amounts)</option>
              <option value="allotment">Allotment (usage bar, no dollars)</option>
            </select>
            <span className="form-hint">How the usage meter renders.</span>
          </div>

          <div className="form-group">
            <label className="form-label">Margin multiplier ({config.margin_multiplier}×)</label>
            <input
              type="number" step="0.05" min="1" className="form-input"
              defaultValue={config.margin_multiplier}
              disabled={!canMutate || saving}
              onBlur={e => {
                const v = parseFloat(e.target.value);
                if (!Number.isNaN(v) && v !== config.margin_multiplier) save({ margin_multiplier: v }, 'Saved');
              }}
            />
            <span className="form-hint">Markup applied to displayed costs (1.0 = true cost).</span>
          </div>
        </div>
      </div>

      {msg && <p className="save-msg">{msg}</p>}
    </div>
  );
}
